/**
 * Room API service for Whakahaere Safety Management System
 *
 * Migrated to typed `@tiaki/http-client` per ADR 113 §Decision.
 */

import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

export interface Room {
  id: number;
  name: string;
  building?: string;
  school?: string;
  status?: string;
  floor?: string;
  safe?: string;
  need_medical_attention?: boolean;
  index?: number;
}

export interface RoomListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Room[];
}

export interface RoomFilters {
  search?: string;
  is_active?: boolean;
  floor?: number;
  page?: number;
  page_size?: number;
  school?: string;
  limit?: number;
}

/**
 * Get list of Rooms with optional filters
 */
export async function getRooms(
  filters: RoomFilters = {},
): Promise<RoomListResponse> {
  const result = await client.GET('/api/v2/room/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as RoomListResponse;
}

/**
 * Get a single Room by ID
 */
export async function getRoomById(id: number): Promise<Room> {
  const result = await client.GET('/api/v2/room/{id}/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as Room;
}

/**
 * Create a new Room
 */
export async function createRoom(data: Room): Promise<Room> {
  const result = await client.POST('/api/v2/room/', {
    body: data as never,
  });
  return unwrap(result) as unknown as Room;
}

/**
 * Update an existing Room
 */
export async function updateRoom(
  id: number,
  data: Partial<Omit<Room, 'id'>>,
): Promise<Room> {
  const result = await client.PATCH('/api/v2/room/{id}/', {
    params: { path: { id } as never },
    body: data as never,
  });
  return unwrap(result) as unknown as Room;
}

/**
 * Delete a Room
 */
export async function deleteRoom(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/room/{id}/', {
    params: { path: { id } as never },
  });
  unwrap(result);
}

/**
 * Create Bulk Room
 */
export async function createBulkRoom(
  data: Array<Omit<Room, 'id'>>,
): Promise<Room[]> {
  const result = await client.POST('/api/v2/bulk-room/', {
    body: data as never,
  });
  return unwrap(result) as unknown as Room[];
}
